import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTenantTranslation } from "hooks/formatters";
import { useState } from "react";
import { ScrapLoadingLayoutGuide } from "./sections/ScrapLoadingLayoutGuide";
import { EventGuide } from "./sections/EventGuide";
import { AreaMappingGuide } from "./sections/AreaMappingGuide";
import { MaterialGuide } from "./sections/MaterialGuide";
import { OtherFunctionalityGuide } from "./sections/OtherFunctionalityGuide";
import {
  IDENTIFY_EVENTS_HOTKEY_SCOPES,
  LABELLING_GUIDE_SCOPE,
} from "components/manualReview/hotkeyScopes";
import { useDisableHotkeyScopes } from "components/scrapEyeLabelling/hooks/useDisableHotkeyScopes";

type LabellingGuideDialogProps = Readonly<{
  open: boolean;
  onClose: () => void;
}>;

type TabSection = 0 | 1 | 2 | 3 | 4;

export const LabellingGuideDialog = ({
  open,
  onClose,
}: LabellingGuideDialogProps) => {
  const { t } = useTenantTranslation();
  const [currentTab, setCurrentTab] = useState<TabSection>(0);
  useDisableHotkeyScopes(
    IDENTIFY_EVENTS_HOTKEY_SCOPES,
    open,
    LABELLING_GUIDE_SCOPE
  );

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabSection) => {
    setCurrentTab(newValue);
  };

  const handlePrevious = () => {
    if (currentTab > 0) {
      setCurrentTab((currentTab - 1) as TabSection);
    }
  };

  const handleNext = () => {
    if (currentTab < 4) {
      setCurrentTab((currentTab + 1) as TabSection);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      slotProps={{
        paper: {
          sx: { height: "90vh" },
        },
      }}
    >
      <DialogTitle>
        {t("labellingGuide")}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label={t("scrapLoadingLayout")} />
        <Tab label={t("events")} />
        <Tab label={t("areaMapping")} />
        <Tab label={t("materialGuide")} />
        <Tab label={t("otherFunctionality")} />
      </Tabs>

      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 64,
            bgcolor:
              currentTab === 0 ? "action.disabledBackground" : "action.hover",
            cursor: currentTab === 0 ? "default" : "pointer",
            transition: "background-color 0.2s",
            "&:hover": {
              bgcolor:
                currentTab === 0
                  ? "action.disabledBackground"
                  : "action.selected",
            },
          }}
          onClick={currentTab === 0 ? undefined : handlePrevious}
        >
          <ChevronLeftIcon
            fontSize="large"
            sx={{
              opacity: currentTab === 0 ? 0.3 : 1,
              color: "text.primary",
            }}
          />
        </Box>

        <DialogContent sx={{ p: 3, overflowY: "auto", flex: 1 }}>
          {currentTab === 0 && <ScrapLoadingLayoutGuide />}
          {currentTab === 1 && <EventGuide />}
          {currentTab === 2 && <AreaMappingGuide />}
          {currentTab === 3 && <MaterialGuide />}
          {currentTab === 4 && <OtherFunctionalityGuide />}
        </DialogContent>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 64,
            bgcolor:
              currentTab === 4 ? "action.disabledBackground" : "action.hover",
            cursor: currentTab === 4 ? "default" : "pointer",
            transition: "background-color 0.2s",
            "&:hover": {
              bgcolor:
                currentTab === 4
                  ? "action.disabledBackground"
                  : "action.selected",
            },
          }}
          onClick={currentTab === 4 ? undefined : handleNext}
        >
          <ChevronRightIcon
            fontSize="large"
            sx={{
              opacity: currentTab === 4 ? 0.3 : 1,
              color: "text.primary",
            }}
          />
        </Box>
      </Box>
    </Dialog>
  );
};
